import {
  Button,
  Input,
  Box,
  Textarea,
  HStack,
  VStack,
  Text,
} from "@chakra-ui/react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/stores/store";
import { MaintenanceRequestWithRelations } from "@/types/maintenance";
import { PropertySelectMenu } from "@/components/shared/property-select-menu";
import { useOrganization } from "@/lib/hooks/use-organization";
import { UserOrgRole } from "@prisma/client";
import { toaster } from "@/components/shared/atoms/toaster";
import { updateMaintenanceRequest } from "@/stores/maintenance-store/maintenance-reducer";
import { IssueTypeSelect } from "./IssueTypeSelect";
import { StatusSelect } from "./StatusSelect";
import { Flyout } from "@/components/shared/flyout";

interface MaintenanceFormData {
  title: string;
  description: string;
  propertyId: string;
  issueType: string;
  status: string;
  preferredContactTimes: {
    days: string[];
    timeRange: {
      start: string;
      end: string;
    };
  };
}

interface MaintenanceFlyoutProps {
  isOpen?: boolean;
  setIsOpen: (open: boolean) => void;
  maintenanceRequestId?: string;
  maintenanceRequests?: MaintenanceRequestWithRelations[];
  setSelectedMaintenanceRequestId?: (id: string | undefined) => void;
  closeFlyout?: () => void;
}

export default function MaintenanceFlyout({
  isOpen,
  setIsOpen,
  maintenanceRequestId,
  maintenanceRequests,
  setSelectedMaintenanceRequestId,
  closeFlyout,
}: MaintenanceFlyoutProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { organization } = useOrganization();

  const emptyFormData = useMemo(
    (): MaintenanceFormData => ({
      title: "",
      description: "",
      propertyId: "",
      issueType: "PLUMBING",
      status: "NEW",
      preferredContactTimes: {
        days: ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"],
        timeRange: {
          start: "09:00",
          end: "17:00",
        },
      },
    }),
    [],
  );

  const maintenanceRequest = maintenanceRequests?.find(
    (mr) => mr.id === maintenanceRequestId,
  );

  const getDefaultFormData = useCallback(
    () =>
      maintenanceRequestId && maintenanceRequest
        ? {
            title: maintenanceRequest.title ?? "",
            description: maintenanceRequest.description ?? "",
            propertyId: maintenanceRequest.propertyId ?? "",
            issueType: maintenanceRequest.issueType ?? "PLUMBING",
            status: maintenanceRequest.status ?? "NEW",
            preferredContactTimes:
              (maintenanceRequest.preferredContactTimes as {
                days: string[];
                timeRange: { start: string; end: string };
              }) ?? {
                days: ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"],
                timeRange: { start: "09:00", end: "17:00" },
              },
          }
        : emptyFormData,
    [emptyFormData, maintenanceRequest, maintenanceRequestId],
  );

  const [formData, setFormData] = useState(getDefaultFormData());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (maintenanceRequestId && maintenanceRequest) {
      setFormData(getDefaultFormData());
    }
  }, [maintenanceRequestId, maintenanceRequest, getDefaultFormData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCancel = () => {
    setFormData(emptyFormData);
    setSelectedMaintenanceRequestId?.(undefined);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData(emptyFormData);
      setSelectedMaintenanceRequestId?.(undefined);
    }
  }, [isOpen, emptyFormData, setSelectedMaintenanceRequestId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (maintenanceRequestId) {
        const response = await fetch(
          `/api/maintenance?id=${maintenanceRequestId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          },
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(
            error.message || "Failed to update maintenance request",
          );
        }

        const result = await response.json();

        // Update the Redux store with the updated maintenance request
        dispatch(updateMaintenanceRequest(result.maintenanceRequest));

        toaster.create({
          title: "Success",
          description: "Maintenance request updated successfully!",
          type: "success",
          duration: 3000,
          closable: true,
        });
      }
      setFormData(emptyFormData);
    } catch (error: unknown) {
      if (error instanceof Error) {
        toaster.create({
          title: "Error",
          description: `Error: ${error.message}`,
          type: "error",
          duration: 5000,
          closable: true,
        });
      } else {
        toaster.create({
          title: "Error",
          description: "An unexpected error occurred.",
          type: "error",
          duration: 5000,
          closable: true,
        });
      }
    } finally {
      setSelectedMaintenanceRequestId?.(undefined);
      setLoading(false);
      closeFlyout?.();
    }
  };

  const isTenant = organization?.userOrgRole === UserOrgRole.TENANT;

  return (
    <Flyout
      isOpen={!!isOpen}
      setIsOpen={setIsOpen}
      title={
        maintenanceRequestId
          ? "Edit Maintenance Request"
          : "Add Maintenance Request"
      }
      maxWidth='800px'
      onClose={closeFlyout}
    >
      <form onSubmit={handleSubmit}>
        <VStack gap={4} align='stretch'>
          <Box>
            <Text as='label' display='block' mb={2} fontWeight={500}>
              Title *
            </Text>
            <Input
              name='title'
              value={formData.title}
              onChange={handleChange}
              placeholder='Enter title'
              required
            />
          </Box>

          <Box>
            <Text as='label' display='block' mb={2} fontWeight={500}>
              Description *
            </Text>
            <Textarea
              name='description'
              value={formData.description}
              onChange={handleChange}
              placeholder='Enter description'
              required
              rows={4}
            />
          </Box>

          <Box>
            <Text as='label' display='block' mb={2} fontWeight={500}>
              Property *
            </Text>
            {isTenant ? (
              <Input
                value={
                  maintenanceRequest?.property?.address ||
                  "Your Current Property"
                }
                readOnly
                disabled
              />
            ) : (
              <PropertySelectMenu
                value={formData.propertyId}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    propertyId: value || "",
                  }))
                }
              />
            )}
          </Box>

          <Box>
            <Text as='label' display='block' mb={2} fontWeight={500}>
              Issue Type *
            </Text>
            <IssueTypeSelect
              value={formData.issueType || "PLUMBING"}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, issueType: value }))
              }
            />
          </Box>

          <Box>
            <Text as='label' display='block' mb={2} fontWeight={500}>
              Status *
            </Text>
            <StatusSelect
              value={formData.status || "NEW"}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, status: value }))
              }
            />
          </Box>

          <Box>
            <Text as='label' display='block' mb={2} fontWeight={500}>
              Preferred Contact Days *
            </Text>
            <HStack gap={2} flexWrap='wrap'>
              {[
                "MONDAY",
                "TUESDAY",
                "WEDNESDAY",
                "THURSDAY",
                "FRIDAY",
                "SATURDAY",
                "SUNDAY",
              ].map((day) => (
                <Box key={day} display='flex' alignItems='center' gap={2}>
                  <input
                    type='checkbox'
                    checked={formData.preferredContactTimes.days.includes(day)}
                    onChange={(e) => {
                      const newDays = e.target.checked
                        ? [...formData.preferredContactTimes.days, day]
                        : formData.preferredContactTimes.days.filter(
                            (d) => d !== day,
                          );
                      setFormData((prev) => ({
                        ...prev,
                        preferredContactTimes: {
                          ...prev.preferredContactTimes,
                          days: newDays,
                        },
                      }));
                    }}
                  />
                  <Text fontSize='sm'>
                    {day.charAt(0) + day.slice(1).toLowerCase()}
                  </Text>
                </Box>
              ))}
            </HStack>
          </Box>

          <Box>
            <Text as='label' display='block' mb={2} fontWeight={500}>
              Preferred Contact Hours *
            </Text>
            <HStack gap={4}>
              <Box flex={1}>
                <Text as='label' display='block' mb={1} fontSize='sm'>
                  Start Time
                </Text>
                <Input
                  type='time'
                  value={formData.preferredContactTimes.timeRange.start}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      preferredContactTimes: {
                        ...prev.preferredContactTimes,
                        timeRange: {
                          ...prev.preferredContactTimes.timeRange,
                          start: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Box>
              <Box flex={1}>
                <Text as='label' display='block' mb={1} fontSize='sm'>
                  End Time
                </Text>
                <Input
                  type='time'
                  value={formData.preferredContactTimes.timeRange.end}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      preferredContactTimes: {
                        ...prev.preferredContactTimes,
                        timeRange: {
                          ...prev.preferredContactTimes.timeRange,
                          end: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Box>
            </HStack>
          </Box>
        </VStack>
        <Box mt={6}>
          <Button variant='outline' mr={3} onClick={handleCancel} type='button'>
            Cancel
          </Button>
          <Button colorScheme='green' type='submit' loading={loading}>
            {loading ? "Saving..." : "Save Maintenance Request"}
          </Button>
        </Box>
      </form>
    </Flyout>
  );
}
